import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  BellRing,
  Building2,
  CalendarCheck2,
  CalendarDays,
  CheckCircle2,
  ClipboardList,
  FileText,
  HeartPulse,
  MessageCircle,
  Microscope,
  Receipt,
  ShieldCheck,
  Stethoscope,
  Syringe,
} from "lucide-react";

const showcaseFeatures = [
  {
    id: "whatsapp",
    icon: MessageCircle,
    eyebrow: "WhatsApp first communication",
    title: "Keep owners informed without adding more admin work.",
    description:
      "CloudVetX turns routine follow ups into a connected workflow, so reminders can move from the clinical record to the owner's WhatsApp conversation without your team rebuilding the message every time.",
    points: [
      "Appointment, vaccination, treatment and surgery reminders",
      "Clinic specific templates and approved variables",
      "Consent, preferred sending hours and delivery status",
    ],
  },
  {
    id: "patient",
    icon: ClipboardList,
    eyebrow: "The complete patient story",
    title: "See the case as one story, not scattered records.",
    description:
      "From the owner's first complaint to examination, diagnostics, treatment and follow up, CloudVetX keeps the information your veterinary team needs connected to the patient.",
    points: [
      "Owner complaints and case history",
      "Examinations, diagnoses and treatment",
      "Lab results, imaging and preventive care",
    ],
  },
  {
    id: "preventive",
    icon: HeartPulse,
    eyebrow: "Preventive care that follows through",
    title: "Know what is due, remind the owner, keep the patient protected.",
    description:
      "CloudVetX connects vaccination and antiparasitic records to the next due date and the reminder workflow, helping the clinic turn preventive care into an actionable routine.",
    points: [
      "Vaccination and antiparasitic history",
      "Clear next due dates",
      "Automated owner reminders tied to care",
    ],
  },
];

const platformPillars = [
  {
    icon: Stethoscope,
    title: "Clinical examination and treatment",
  },
  {
    icon: Microscope,
    title: "Laboratory diagnostics and medical imaging",
  },
  {
    icon: Receipt,
    title: "Inventory and billing",
  },
  {
    icon: Building2,
    title: "Multi branch practice management",
  },
];

const easeOut = [0.22, 1, 0.36, 1];

const revealMotion = (reducedMotion, delay = 0, distance = 18) => ({
  initial: reducedMotion ? false : { opacity: 0, y: distance },
  whileInView: reducedMotion ? undefined : { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.35 },
  transition: { duration: 0.58, delay, ease: easeOut },
});

const WindowChrome = ({ label, dark = false }) => (
  <div
    className={`flex items-center gap-2 border-b px-4 py-3 sm:px-5 ${
      dark
        ? "border-white/10 bg-white/[0.04] text-emerald-50"
        : "border-emerald-950/10 bg-white text-emerald-950"
    }`}
  >
    <span className={`h-2 w-2 rounded-full ${dark ? "bg-white/35" : "bg-emerald-900/20"}`} />
    <span className={`h-2 w-2 rounded-full ${dark ? "bg-white/25" : "bg-emerald-900/15"}`} />
    <span className={`h-2 w-2 rounded-full ${dark ? "bg-white/20" : "bg-emerald-900/10"}`} />
    <span className="ml-auto text-[10px] font-bold uppercase tracking-[0.18em] opacity-65">
      {label}
    </span>
  </div>
);

const WhatsAppVisual = ({ reducedMotion }) => (
  <div
    className="relative overflow-hidden rounded-[1.75rem] border border-white/10 bg-[#073c33] shadow-[0_28px_70px_rgba(3,44,36,0.24)]"
    aria-hidden="true"
  >
    <WindowChrome label="Owner communication" dark />

    <div className="relative p-4 sm:p-6 lg:p-7">
      <div className="absolute right-5 top-5 h-28 w-28 rounded-full bg-emerald-300/10 blur-3xl" />

      <div className="relative grid gap-4 sm:grid-cols-[0.92fr_1.08fr] sm:gap-5">
        <div className="space-y-3">
          <motion.div
            {...revealMotion(reducedMotion, 0.06, 12)}
            className="rounded-2xl border border-white/10 bg-white/[0.08] p-4 text-white backdrop-blur-sm"
          >
            <div className="mb-4 flex items-center justify-between gap-3">
              <span className="text-[10px] font-bold uppercase tracking-[0.17em] text-emerald-200">
                Care event
              </span>
              <span className="rounded-full bg-white/10 px-2.5 py-1 text-[10px] font-semibold text-white/80">
                Vaccination
              </span>
            </div>
            <div className="flex items-center gap-3">
              <span className="grid h-11 w-11 place-items-center rounded-xl bg-emerald-200 text-emerald-950">
                <CalendarCheck2 size={20} />
              </span>
              <div>
                <p className="text-sm font-semibold">Next dose due</p>
                <p className="mt-1 text-xs text-white/60">14 October · Luna</p>
              </div>
            </div>
          </motion.div>

          <div className="relative pl-5">
            <motion.div
              className="absolute left-[9px] top-[-12px] h-[calc(100%_+_12px)] w-px origin-top bg-emerald-200/45"
              initial={reducedMotion ? false : { scaleY: 0 }}
              whileInView={reducedMotion ? undefined : { scaleY: 1 }}
              viewport={{ once: true, amount: 0.55 }}
              transition={{ duration: 0.7, delay: 0.2, ease: easeOut }}
            />
            <motion.span
              className="absolute left-[5px] top-5 h-2.5 w-2.5 rounded-full border-2 border-[#073c33] bg-emerald-200"
              initial={reducedMotion ? false : { opacity: 0, scale: 0.7 }}
              whileInView={reducedMotion ? undefined : { opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.55 }}
              transition={{ duration: 0.3, delay: 0.42 }}
            />
            <motion.div
              {...revealMotion(reducedMotion, 0.28, 12)}
              className="rounded-2xl border border-white/10 bg-white p-4 text-emerald-950 shadow-[0_18px_45px_rgba(1,26,21,0.24)]"
            >
              <div className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-emerald-700">
                <BellRing size={15} /> Reminder prepared
              </div>
              <div className="rounded-xl bg-[#f1f6ee] p-3 text-xs leading-relaxed text-slate-600">
                Luna's vaccination is due on 14 October. Please contact the clinic if you need to reschedule.
              </div>
              <div className="mt-3 flex items-center justify-between text-[10px] font-semibold text-slate-400">
                <span>Approved template</span>
                <span>09:00 sending window</span>
              </div>
            </motion.div>
          </div>
        </div>

        <div className="relative min-h-[300px] overflow-hidden rounded-[1.4rem] border border-white/10 bg-[#e9f2e8] p-4 sm:p-5">
          <div className="flex items-center gap-3 border-b border-emerald-950/10 pb-4">
            <span className="grid h-9 w-9 place-items-center rounded-full bg-emerald-800 text-xs font-bold text-white">
              L
            </span>
            <div>
              <p className="text-sm font-semibold text-emerald-950">Luna's owner</p>
              <p className="text-[11px] text-slate-500">WhatsApp conversation</p>
            </div>
          </div>

          <motion.div
            className="my-4 flex items-center gap-3"
            initial={reducedMotion ? false : { opacity: 0, x: -12 }}
            whileInView={reducedMotion ? undefined : { opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.45 }}
            transition={{ duration: 0.45, delay: 0.46, ease: easeOut }}
          >
            <motion.span
              className="h-px flex-1 origin-left bg-emerald-800/25"
              initial={reducedMotion ? false : { scaleX: 0 }}
              whileInView={reducedMotion ? undefined : { scaleX: 1 }}
              viewport={{ once: true, amount: 0.45 }}
              transition={{ duration: 0.55, delay: 0.48, ease: easeOut }}
            />
            <ArrowRight size={16} className="text-emerald-700/60" />
          </motion.div>

          <motion.div
            {...revealMotion(reducedMotion, 0.58, 14)}
            className="ml-auto max-w-[92%] rounded-2xl rounded-tr-md bg-[#d7f3dc] p-4 shadow-sm"
          >
            <div className="mb-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-800">
              <MessageCircle size={14} /> WhatsApp
            </div>
            <p className="text-sm leading-relaxed text-emerald-950">
              Luna's vaccination is due on 14 October. Please contact the clinic if you need to reschedule.
            </p>
            <div className="mt-3 flex items-center justify-end gap-1.5 text-[10px] font-semibold text-emerald-700">
              <CheckCircle2 size={13} /> Delivered
            </div>
          </motion.div>

          <motion.div
            {...revealMotion(reducedMotion, 0.74, 10)}
            className="absolute bottom-4 left-4 right-4 flex items-center justify-between rounded-xl border border-emerald-900/10 bg-white/85 px-3 py-2.5 text-[11px] backdrop-blur"
          >
            <span className="font-semibold text-emerald-950">Delivery state visible in CloudVetX</span>
            <span className="flex items-center gap-1 font-semibold text-emerald-700">
              <CheckCircle2 size={13} /> Sent
            </span>
          </motion.div>
        </div>
      </div>
    </div>
  </div>
);

const storyStages = [
  { icon: FileText, label: "Owner complaint", detail: "Reason for visit and case history" },
  { icon: Stethoscope, label: "Examination", detail: "Clinical findings and diagnosis" },
  { icon: Microscope, label: "Diagnostics", detail: "Laboratory results and imaging" },
  { icon: ClipboardList, label: "Treatment", detail: "Plan, medication and care" },
  { icon: CheckCircle2, label: "Follow up", detail: "Next steps remain with the case" },
];

const PatientStoryVisual = ({ reducedMotion }) => (
  <div
    className="overflow-hidden rounded-[1.75rem] border border-emerald-950/10 bg-white shadow-[0_24px_65px_rgba(5,55,43,0.11)]"
    aria-hidden="true"
  >
    <WindowChrome label="Patient timeline" />

    <div className="grid gap-5 bg-[#f5f8f3] p-4 sm:p-6 lg:grid-cols-[1.15fr_0.85fr] lg:p-7">
      <div className="rounded-[1.4rem] border border-emerald-950/10 bg-white p-4 sm:p-5">
        <div className="flex items-center gap-3 border-b border-emerald-950/10 pb-4">
          <span className="grid h-11 w-11 place-items-center rounded-2xl bg-emerald-950 text-sm font-bold text-white">
            M
          </span>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-semibold text-emerald-950">Milo</p>
              <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-emerald-700">
                Current visit
              </span>
            </div>
            <p className="mt-1 text-xs text-slate-500">Patient record · one connected case</p>
          </div>
        </div>

        <div className="relative mt-5 space-y-1 pl-2">
          <motion.div
            className="absolute left-[20px] top-5 h-[calc(100%_-_40px)] w-px origin-top bg-emerald-800/20"
            initial={reducedMotion ? false : { scaleY: 0 }}
            whileInView={reducedMotion ? undefined : { scaleY: 1 }}
            viewport={{ once: true, amount: 0.35 }}
            transition={{ duration: 0.9, delay: 0.12, ease: easeOut }}
          />

          {storyStages.map((stage, index) => {
            const Icon = stage.icon;
            return (
              <motion.div
                key={stage.label}
                {...revealMotion(reducedMotion, 0.12 + index * 0.09, 10)}
                className="relative flex gap-3 rounded-xl px-2 py-3 transition-colors duration-200 hover:bg-emerald-50/70"
              >
                <span className="relative z-10 grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-emerald-900/10 bg-[#edf5ec] text-emerald-800">
                  <Icon size={17} />
                </span>
                <div className="min-w-0 pt-0.5">
                  <p className="text-sm font-semibold text-emerald-950">{stage.label}</p>
                  <p className="mt-1 text-xs leading-relaxed text-slate-500">{stage.detail}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <motion.div
          {...revealMotion(reducedMotion, 0.22, 14)}
          className="rounded-[1.35rem] bg-[#073c33] p-5 text-white shadow-[0_18px_42px_rgba(4,55,44,0.16)]"
        >
          <div className="mb-6 flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-[0.17em] text-emerald-200">
              Visit context
            </span>
            <ClipboardList size={18} className="text-emerald-200" />
          </div>
          <p className="font-serif text-2xl leading-tight text-white">The record keeps the clinical story together.</p>
          <p className="mt-3 text-xs leading-relaxed text-emerald-50/65">
            Complaints, findings, diagnostics and treatment remain anchored to the patient and visit.
          </p>
        </motion.div>

        <motion.div
          {...revealMotion(reducedMotion, 0.48, 14)}
          className="rounded-[1.35rem] border border-emerald-950/10 bg-white p-5"
        >
          <span className="text-[10px] font-bold uppercase tracking-[0.17em] text-emerald-700">At a glance</span>
          <div className="mt-4 space-y-3">
            {[
              ["History", "Available"],
              ["Diagnostics", "Linked"],
              ["Follow up", "Visible"],
            ].map(([label, value], index) => (
              <motion.div
                key={label}
                initial={reducedMotion ? false : { opacity: 0, x: 10 }}
                whileInView={reducedMotion ? undefined : { opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ duration: 0.35, delay: 0.58 + index * 0.08, ease: easeOut }}
                className="flex items-center justify-between gap-4 border-b border-emerald-950/10 pb-3 last:border-0 last:pb-0"
              >
                <span className="text-xs text-slate-500">{label}</span>
                <span className="flex items-center gap-1.5 text-xs font-semibold text-emerald-800">
                  <CheckCircle2 size={14} /> {value}
                </span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  </div>
);

const PreventiveVisual = ({ reducedMotion }) => (
  <div
    className="overflow-hidden rounded-[1.75rem] border border-emerald-950/10 bg-[#edf4eb] shadow-[0_24px_65px_rgba(5,55,43,0.11)]"
    aria-hidden="true"
  >
    <WindowChrome label="Preventive care" />

    <div className="p-4 sm:p-6 lg:p-7">
      <div className="grid gap-4 lg:grid-cols-[0.82fr_1.18fr]">
        <motion.div
          {...revealMotion(reducedMotion, 0.08, 14)}
          className="flex flex-col justify-between rounded-[1.4rem] bg-[#073c33] p-5 text-white sm:p-6"
        >
          <div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-[10px] font-bold uppercase tracking-[0.17em] text-emerald-200">
                Next due date
              </span>
              <CalendarDays size={19} className="text-emerald-200" />
            </div>
            <div className="mt-8">
              <p className="font-serif text-5xl leading-none sm:text-6xl">14</p>
              <p className="mt-2 text-lg font-semibold text-emerald-100">October</p>
              <p className="mt-1 text-xs text-white/55">Luna · vaccination</p>
            </div>
          </div>
          <div className="mt-8 border-t border-white/10 pt-4">
            <div className="flex items-center gap-2 text-xs text-emerald-100">
              <Syringe size={16} /> Preventive treatment recorded
            </div>
          </div>
        </motion.div>

        <div className="space-y-4">
          <motion.div
            {...revealMotion(reducedMotion, 0.2, 12)}
            className="rounded-[1.35rem] border border-emerald-950/10 bg-white p-4 sm:p-5"
          >
            <div className="mb-4 flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-[0.17em] text-emerald-700">
                Preventive history
              </span>
              <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-semibold text-emerald-700">
                Up to date
              </span>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-3 rounded-xl bg-[#f5f8f3] p-3">
                <span className="grid h-9 w-9 place-items-center rounded-lg bg-white text-emerald-800 shadow-sm">
                  <Syringe size={17} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-emerald-950">Vaccination</p>
                  <p className="mt-0.5 text-[11px] text-slate-500">Recorded · next due 14 October</p>
                </div>
                <CheckCircle2 size={16} className="text-emerald-700" />
              </div>
              <div className="flex items-center gap-3 rounded-xl bg-[#f5f8f3] p-3">
                <span className="grid h-9 w-9 place-items-center rounded-lg bg-white text-emerald-800 shadow-sm">
                  <ShieldCheck size={17} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-emerald-950">Antiparasitic care</p>
                  <p className="mt-0.5 text-[11px] text-slate-500">History stays with the patient</p>
                </div>
                <CheckCircle2 size={16} className="text-emerald-700" />
              </div>
            </div>
          </motion.div>

          <div className="grid gap-3 sm:grid-cols-2">
            <motion.div
              {...revealMotion(reducedMotion, 0.38, 10)}
              className="rounded-[1.2rem] border border-emerald-950/10 bg-white p-4"
            >
              <div className="mb-4 flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-800">
                <BellRing size={17} />
              </div>
              <p className="text-xs font-semibold text-emerald-950">Owner reminder prepared</p>
              <p className="mt-1.5 text-[11px] leading-relaxed text-slate-500">The due date enters the reminder workflow.</p>
            </motion.div>

            <motion.div
              {...revealMotion(reducedMotion, 0.54, 10)}
              className="rounded-[1.2rem] border border-emerald-950/10 bg-white p-4"
            >
              <div className="mb-4 flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-800">
                <MessageCircle size={17} />
              </div>
              <p className="text-xs font-semibold text-emerald-950">History stays current</p>
              <p className="mt-1.5 text-[11px] leading-relaxed text-slate-500">The care record and next action remain connected.</p>
            </motion.div>
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2 px-1">
        {["Care recorded", "Due date", "Owner reminder", "Updated history"].map((label, index) => (
          <React.Fragment key={label}>
            <motion.span
              initial={reducedMotion ? false : { opacity: 0 }}
              whileInView={reducedMotion ? undefined : { opacity: 1 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.3, delay: 0.18 + index * 0.12 }}
              className={`text-[9px] font-bold uppercase tracking-[0.12em] text-emerald-800/60 ${
                index > 1 ? "hidden sm:inline" : ""
              }`}
            >
              {label}
            </motion.span>
            {index < 3 && (
              <motion.span
                className={`h-px flex-1 origin-left bg-emerald-800/20 ${index > 1 ? "hidden sm:block" : ""}`}
                initial={reducedMotion ? false : { scaleX: 0 }}
                whileInView={reducedMotion ? undefined : { scaleX: 1 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ duration: 0.45, delay: 0.24 + index * 0.12, ease: easeOut }}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  </div>
);

const FeatureVisual = ({ feature, reducedMotion }) => {
  if (feature.id === "whatsapp") {
    return <WhatsAppVisual reducedMotion={reducedMotion} />;
  }

  if (feature.id === "patient") {
    return <PatientStoryVisual reducedMotion={reducedMotion} />;
  }

  return <PreventiveVisual reducedMotion={reducedMotion} />;
};

const FeatureCopy = ({ feature, reducedMotion }) => {
  const Icon = feature.icon;

  return (
    <div className="cvx-workflow-copy flex h-full flex-col justify-center py-2">
      <motion.div
        {...revealMotion(reducedMotion, 0.02, 12)}
        className="cvx-workflow-label mb-7 flex items-center gap-4"
      >
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-950 text-white shadow-[0_10px_24px_rgba(5,55,43,0.16)]">
          <Icon size={23} aria-hidden="true" />
        </span>
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700">
          {feature.eyebrow}
        </p>
      </motion.div>

      <motion.h3
        {...revealMotion(reducedMotion, 0.08, 14)}
        className="cvx-workflow-title max-w-xl font-serif text-emerald-950"
      >
        {feature.title}
      </motion.h3>

      <motion.p
        {...revealMotion(reducedMotion, 0.14, 14)}
        className="cvx-workflow-description mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
      >
        {feature.description}
      </motion.p>

      <motion.ul
        {...revealMotion(reducedMotion, 0.2, 14)}
        className="cvx-workflow-points mt-8 grid gap-3 sm:grid-cols-3 lg:grid-cols-1"
      >
        {feature.points.map((point) => (
          <li key={point} className="flex items-start gap-3 text-sm leading-relaxed text-slate-700">
            <CheckCircle2
              size={18}
              className="mt-0.5 shrink-0 text-emerald-700"
              aria-hidden="true"
            />
            <span>{point}</span>
          </li>
        ))}
      </motion.ul>
    </div>
  );
};

const Features = () => {
  const reducedMotion = useReducedMotion();

  return (
    <section id="features" className="cvx-section cvx-features overflow-hidden">
      <div className="container mx-auto px-5 sm:px-8">
        <motion.div
          {...revealMotion(reducedMotion, 0, 18)}
          className="cvx-features-intro max-w-4xl"
        >
          <p className="cvx-kicker mb-5">Why CloudVetX</p>
          <h2 className="cvx-section-title mb-6">
            Three workflows built to make everyday veterinary work feel simpler.
          </h2>
          <p className="max-w-3xl text-lg leading-relaxed text-slate-600">
            CloudVetX is more than a checklist of practice management features.
            These are the connected workflows we believe are worth putting front
            and centre.
          </p>
        </motion.div>

        <div className="cvx-workflows">
          {showcaseFeatures.map((feature, index) => {
            const reverse = index % 2 === 1;

            return (
              <motion.article
                key={feature.id}
                initial={reducedMotion ? false : { opacity: 0, y: 22 }}
                whileInView={reducedMotion ? undefined : { opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.12 }}
                transition={{ duration: 0.6, ease: easeOut }}
                className="relative"
              >
                <div
                  className={`cvx-workflow-row grid items-center gap-8 lg:grid-cols-12 ${
                    reverse ? "lg:[&>*:first-child]:order-2" : ""
                  }`}
                >
                  <div className="lg:col-span-5">
                    <FeatureCopy feature={feature} reducedMotion={reducedMotion} />
                  </div>

                  <div className="relative lg:col-span-7">
                    <div
                      className={`absolute inset-4 rounded-[2.25rem] border border-emerald-950/5 ${
                        feature.id === "whatsapp" ? "bg-[#dfeadf]" : "bg-white/70"
                      }`}
                      aria-hidden="true"
                    />
                    <motion.div
                      className="relative"
                      initial={reducedMotion ? false : { opacity: 0, scale: 0.985 }}
                      whileInView={reducedMotion ? undefined : { opacity: 1, scale: 1 }}
                      viewport={{ once: true, amount: 0.2 }}
                      transition={{ duration: 0.72, delay: 0.08, ease: easeOut }}
                    >
                      <FeatureVisual feature={feature} reducedMotion={reducedMotion} />
                    </motion.div>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>

        <motion.div
          {...revealMotion(reducedMotion, 0, 18)}
          className="cvx-features-bridge border-t border-emerald-950/10 pt-10"
        >
          <div className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-emerald-700">
                And the platform behind them
              </p>
              <h3 className="font-serif text-2xl tracking-[-0.02em] text-emerald-950 sm:text-3xl">
                The rest of the clinic workflow stays connected too.
              </h3>
            </div>

            <a
              href="/#contact"
              className="inline-flex items-center gap-2 whitespace-nowrap font-semibold text-emerald-800 hover:underline"
            >
              Request a walkthrough
              <ArrowUpRight size={18} aria-hidden="true" />
            </a>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {platformPillars.map((feature) => {
              const Icon = feature.icon;

              return (
                <div
                  key={feature.title}
                  className="group flex items-center gap-4 rounded-2xl border border-emerald-950/10 bg-white/70 p-4 transition duration-200 hover:border-emerald-800/25 hover:bg-white sm:p-5"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#e7f5e9] text-[#11664c] transition-transform duration-200 group-hover:-translate-y-0.5">
                    <Icon size={20} aria-hidden="true" />
                  </span>
                  <p className="text-sm font-semibold leading-snug text-emerald-950 sm:text-base">
                    {feature.title}
                  </p>
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Features;
